import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { 
  Mail, 
  FolderOpen,
  Camera,
  Megaphone,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Clock,
  Calendar,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Building2,
  Loader2,
  Circle,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import { authFetch } from '../lib/api';
import { SavedSearchPills } from '../components/SavedSearchPills';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const DashboardPage = () => {
  const { isBarbiePink } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [milestones, setMilestones] = useState({ overdue: [], upcoming: [] });
  const [milestonePhases, setMilestonePhases] = useState({ marketing: [], production: [], feedback: [] });
  const [loading, setLoading] = useState(true);
  const [milestonesLoading, setMilestonesLoading] = useState(true);
  const [phasesLoading, setPhasesLoading] = useState(true);
  const [milestoneReportOpen, setMilestoneReportOpen] = useState(false); // Default closed
  const [serviceStatus, setServiceStatus] = useState({ gmail: null, drive: null, checked: false });
  const gmailCheckDone = useRef(false);
  
  // Workflow detail dialog state
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  
  // Per-workflow expanded state — one entry per workflow_id, true when
  // expanded.
  const [expandedWorkflows, setExpandedWorkflows] = useState({});

  // Group ALL milestones (overdue + upcoming) by workflow so the user sees
  // one row per workflow waiting in line; each row can expand to show every
  // milestone inside that workflow.
  // IMPORTANT: useMemo must be called before any early returns.
  const groupedMilestones = useMemo(() => {
    const overdue = milestones.overdue || [];
    const upcoming = milestones.upcoming || [];

    // Combine — overdue first, then upcoming, all keyed by workflow_id.
    const byWorkflow = new Map();
    [...overdue, ...upcoming].forEach((m) => {
      const wfId = m.workflow_id || `__no_wf_${m.milestone_id}`;
      if (!byWorkflow.has(wfId)) byWorkflow.set(wfId, []);
      byWorkflow.get(wfId).push(m);
    });

    const rows = [];
    byWorkflow.forEach((items, wfId) => {
      // Sort each workflow's items: overdue (most overdue first) then upcoming
      // by due_date ascending.
      items.sort((a, b) => {
        if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      const headline = items[0];
      const overdueCount = items.filter((m) => m.is_overdue).length;
      const upcomingCount = items.length - overdueCount;
      rows.push({
        ...headline,
        workflow_id: wfId,
        items,
        overdue_count: overdueCount,
        upcoming_count: upcomingCount,
        total_count: items.length,
      });
    });

    // Sort rows: workflows with overdue items first (most overdue first),
    // then by the headline's days_until ascending.
    rows.sort((a, b) => {
      if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
      return a.days_until - b.days_until;
    });
    return rows;
  }, [milestones.overdue, milestones.upcoming]);

  const toggleWorkflowExpanded = (wfId) => {
    setExpandedWorkflows((prev) => ({ ...prev, [wfId]: !prev[wfId] }));
  };

  useEffect(() => {
    fetchStats();
    fetchMilestones();
    fetchMilestonePhases();
    checkServiceConnections();
  }, []);

  // Check Gmail and Drive connection status
  const checkServiceConnections = async () => {
    try {
      const response = await authFetch(`${API}/gmail/status`);
      if (response.ok) {
        const status = await response.json();
        setServiceStatus(prev => ({
          ...prev,
          gmail: status.connected,
          drive: status.drive_connected || status.connected, // Drive uses same auth
          checked: true
        }));
      }
    } catch (err) {
      console.error('Service check failed:', err);
      setServiceStatus(prev => ({ ...prev, checked: true }));
    }
  };

  // Connect to Google services
  const connectGoogleServices = async () => {
    try {
      const response = await authFetch(`${API}/gmail/connect`);
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        }
      }
    } catch (err) {
      console.error('Failed to connect:', err);
    }
  };

  // Check Gmail status - manual trigger only, not auto-redirect
  const checkAndConnectGmail = async () => {
    try {
      const response = await authFetch(`${API}/gmail/status`);
      if (response.ok) {
        const status = await response.json();
        // Just log the status, don't auto-redirect
        console.log('Gmail status:', status.connected ? 'Connected' : 'Not connected');
      }
    } catch (err) {
      console.error('Gmail check failed:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API}/dashboard/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await authFetch(`${API}/dashboard/upcoming-milestones?days=14`);
      if (response.ok) {
        const data = await response.json();
        setMilestones(data);
      }
    } catch (err) {
      console.error('Failed to fetch milestones:', err);
    } finally {
      setMilestonesLoading(false);
    }
  };

  const fetchMilestonePhases = async () => {
    try {
      const response = await authFetch(`${API}/dashboard/milestone-phases`);
      if (response.ok) {
        const data = await response.json();
        setMilestonePhases(data);
      }
    } catch (err) {
      console.error('Failed to fetch milestone phases:', err);
    } finally {
      setPhasesLoading(false);
    }
  };

  // Open workflow detail dialog
  const openWorkflowDialog = async (workflowId) => {
    setWorkflowLoading(true);
    setWorkflowDialogOpen(true);
    try {
      const response = await authFetch(`${API}/workflows/${workflowId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedWorkflow(data);
      } else {
        toast.error('Failed to load workflow details');
      }
    } catch (err) {
      console.error('Failed to fetch workflow:', err);
      toast.error('Failed to load workflow details');
    } finally {
      setWorkflowLoading(false);
    }
  };

  // Calculate workflow progress
  const calculateProgress = (milestones) => {
    if (!milestones || milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.status === 'completed').length;
    return Math.round((completed / milestones.length) * 100);
  };

  // Get milestone due date based on photography date
  const getMilestoneDueDate = (workflow, milestone) => {
    if (!workflow?.photography_date || milestone.due_days === undefined) return null;
    const photoDate = new Date(workflow.photography_date);
    const dueDate = new Date(photoDate);
    dueDate.setDate(dueDate.getDate() + milestone.due_days);
    return dueDate;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      on_hold: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.pending;
  };

  const getCategoryColor = (category) => {
    const colors = {
      marketing: 'bg-purple-100 text-purple-700 border-purple-200',
      prephotoday: 'bg-orange-100 text-orange-700 border-orange-200',
      production: 'bg-blue-100 text-blue-700 border-blue-200',
      feedback: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    return colors[category] || colors.production;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      marketing: 'Marketing',
      prephotoday: 'Schedule',
      production: 'Production',
      feedback: 'Feedback'
    };
    return labels[category] || category;
  };

  const getDaysLabel = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return '1 day overdue';
    if (days < 0) return `${Math.abs(days)} days overdue`;
    return `In ${days} days`;
  };

  const categories = [
    {
      key: 'marketing',
      label: 'Marketing',
      icon: Megaphone,
      gradient: 'from-purple-500 to-purple-600',
      bgLight: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-600',
      description: 'Promotions & outreach'
    },
    {
      key: 'prephotoday',
      label: 'Schedule',
      icon: Calendar,
      gradient: 'from-orange-500 to-orange-600',
      bgLight: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-600',
      description: 'Pre-photography prep'
    },
    {
      key: 'production',
      label: 'Production',
      icon: Camera,
      gradient: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      description: 'Photo shoots & editing'
    },
    {
      key: 'feedback',
      label: 'Feedback',
      icon: MessageSquare,
      gradient: 'from-amber-500 to-amber-600',
      bgLight: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-600',
      description: 'Reviews & follow-ups'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-8 p-2">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-72 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const totalActive = (stats?.category_stats?.marketing || 0) + 
                      (stats?.category_stats?.prephotoday || 0) +
                      (stats?.category_stats?.production || 0) + 
                      (stats?.category_stats?.feedback || 0);

  const allMilestones = [...milestones.overdue, ...milestones.upcoming];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Service Connection Banner - Prominent for first-time users */}
      {serviceStatus.checked && (!serviceStatus.gmail || !serviceStatus.drive) && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-full">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-amber-900 dark:text-amber-100">
                  Connect Google Services Required
                </h3>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  To use all features of G.S Workflow, please connect your Google account to enable:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-600 dark:text-amber-400">
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email sending via Gmail
                  </li>
                  <li className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" /> Document templates on Google Drive
                  </li>
                  <li className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Calendar integration
                  </li>
                  <li className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> AI chat history storage
                  </li>
                </ul>
              </div>
            </div>
            <Button
              size="lg"
              onClick={connectGoogleServices}
              className="bg-amber-600 hover:bg-amber-700 text-white shadow-md"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Connect Google Now
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Overview of your workflow activity
        </p>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* 1. Active Workflows */}
        <Card 
          className={cn(
            "cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
            isBarbiePink && "card-brutalist"
          )}
          onClick={() => navigate('/workflows')}
          data-testid="stat-active-workflows"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active</p>
                <p className="text-2xl font-bold mt-1">{totalActive}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-100">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. In Progress */}
        <Card 
          className={cn(
            "cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
            isBarbiePink && "card-brutalist"
          )}
          onClick={() => navigate('/workflows')}
          data-testid="stat-in-progress"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">In Progress</p>
                <p className="text-2xl font-bold mt-1">{stats?.workflow_stats?.in_progress || 0}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Total Workflows */}
        <Card 
          className={cn(
            "cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
            isBarbiePink && "card-brutalist"
          )}
          onClick={() => navigate('/workflows')}
          data-testid="stat-total-workflows"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Workflows</p>
                <p className="text-2xl font-bold mt-1">{stats?.total_workflows || 0}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-100">
                <FolderOpen className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Emails Sent */}
        <Card 
          className={cn(
            "cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
            isBarbiePink && "card-brutalist"
          )}
          onClick={() => navigate('/email-audit')}
          data-testid="stat-emails-sent"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Emails Sent</p>
                <p className="text-2xl font-bold mt-1">{stats?.total_emails_sent || 0}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-purple-100">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Frequently-used Saved Searches — quick-jump pills */}
      <SavedSearchPills navigate={navigate} isBarbiePink={isBarbiePink} />

      {/* Milestone Phases - 3 Column Layout */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
          Progress Updates
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Marketing Column */}
          <Card 
            data-testid="phase-card-marketing"
            className={cn(
              "overflow-hidden border-amber-200",
              isBarbiePink && "card-brutalist"
            )}
          >
            <div className="px-5 py-4 border-b bg-amber-50 border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                    <Megaphone className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Marketing</h3>
                    <p className="text-xs text-muted-foreground">Before Photography</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-amber-600">
                  {milestonePhases.total_marketing || 0}
                </span>
              </div>
            </div>
            <CardContent className="p-0 max-h-[300px] overflow-y-auto">
              {phasesLoading ? (
                <div className="px-5 py-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : milestonePhases.marketing?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {milestonePhases.marketing.map((item) => (
                    <div 
                      key={`${item.org_id}-${item.milestone_id}`}
                      className="px-4 py-3 hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => openWorkflowDialog(item.workflow_id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate text-foreground">{item.org_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.milestone_name}</p>
                        </div>
                        <Badge variant="destructive" className="text-xs shrink-0">
                          {item.days_overdue}d
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-5 py-8 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-muted-foreground">No overdue milestones</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Production Column */}
          <Card 
            data-testid="phase-card-production"
            className={cn(
              "overflow-hidden border-blue-200",
              isBarbiePink && "card-brutalist"
            )}
          >
            <div className="px-5 py-4 border-b bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                    <Camera className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Production</h3>
                    <p className="text-xs text-muted-foreground">Photo to Dispatch</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {milestonePhases.total_production || 0}
                </span>
              </div>
            </div>
            <CardContent className="p-0 max-h-[300px] overflow-y-auto">
              {phasesLoading ? (
                <div className="px-5 py-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : milestonePhases.production?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {milestonePhases.production.map((item) => (
                    <div 
                      key={`${item.org_id}-${item.milestone_id}`}
                      className="px-4 py-3 hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => openWorkflowDialog(item.workflow_id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate text-foreground">{item.org_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.milestone_name}</p>
                        </div>
                        <Badge variant="destructive" className="text-xs shrink-0">
                          {item.days_overdue}d
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-5 py-8 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-muted-foreground">No overdue milestones</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback Column */}
          <Card 
            data-testid="phase-card-feedback"
            className={cn(
              "overflow-hidden border-emerald-200",
              isBarbiePink && "card-brutalist"
            )}
          >
            <div className="px-5 py-4 border-b bg-emerald-50 border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Feedback</h3>
                    <p className="text-xs text-muted-foreground">After Dispatch</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-emerald-600">
                  {milestonePhases.total_feedback || 0}
                </span>
              </div>
            </div>
            <CardContent className="p-0 max-h-[300px] overflow-y-auto">
              {phasesLoading ? (
                <div className="px-5 py-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : milestonePhases.feedback?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {milestonePhases.feedback.map((item) => (
                    <div 
                      key={`${item.org_id}-${item.milestone_id}`}
                      className="px-4 py-3 hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => openWorkflowDialog(item.workflow_id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate text-foreground">{item.org_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.milestone_name}</p>
                        </div>
                        <Badge variant="destructive" className="text-xs shrink-0">
                          {item.days_overdue}d
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-5 py-8 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-muted-foreground">No overdue milestones</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Workflow Detail Dialog */}
      <Dialog open={workflowDialogOpen} onOpenChange={setWorkflowDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              {selectedWorkflow?.name || 'Workflow Details'}
            </DialogTitle>
          </DialogHeader>
          
          {workflowLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedWorkflow && (
            <div className="space-y-6">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge className={getStatusColor(selectedWorkflow.status)}>
                    {(selectedWorkflow.status || 'pending').replace('_', ' ')}
                  </Badge>
                  <span className="text-xl font-bold">
                    {calculateProgress(selectedWorkflow.milestones)}% Complete
                  </span>
                </div>
                <Progress value={calculateProgress(selectedWorkflow.milestones)} className="h-3" />
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Organization</p>
                  <p className="font-medium text-sm">{selectedWorkflow.org_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Category</p>
                  <p className="font-medium text-sm capitalize">{selectedWorkflow.category || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Photography Date</p>
                  <p className="font-medium text-sm">
                    {selectedWorkflow.photography_date 
                      ? new Date(selectedWorkflow.photography_date).toLocaleDateString('en-AU', { 
                          day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney'
                        })
                      : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Year</p>
                  <p className="font-medium text-sm">{selectedWorkflow.year || 'N/A'}</p>
                </div>
              </div>

              <Separator />

              {/* Milestones List */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Milestones
                </h3>
                <div className="space-y-2">
                  {selectedWorkflow.milestones?.map((ms) => {
                    const dueDate = getMilestoneDueDate(selectedWorkflow, ms);
                    const isOverdue = dueDate && new Date() > dueDate && ms.status !== 'completed';
                    
                    return (
                      <div 
                        key={ms.milestone_id} 
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border",
                          ms.status === 'completed' ? 'bg-emerald-50/50 border-emerald-200' :
                          isOverdue ? 'bg-red-50/50 border-red-200' : 'bg-slate-50/50 border-slate-200'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {ms.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          ) : isOverdue ? (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-400" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{ms.name}</p>
                            {dueDate && (
                              <p className="text-xs text-muted-foreground">
                                Due: {dueDate.toLocaleDateString('en-AU', { 
                                  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney'
                                })}
                                {ms.due_days !== undefined && (
                                  <span className="ml-2 text-slate-500">
                                    ({ms.due_days > 0 ? `+${ms.due_days}` : ms.due_days} days)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge 
                          variant={ms.status === 'completed' ? 'default' : isOverdue ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {ms.status === 'completed' ? 'Done' : isOverdue ? 'Overdue' : 'Pending'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Workflow Notes */}
              {selectedWorkflow.notes && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-2">Notes</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedWorkflow.notes}</p>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => selectedWorkflow && navigate(`/organizations/${selectedWorkflow.org_id}`)}
            >
              <Building2 className="w-4 h-4 mr-2" />
              View Organization
            </Button>
            <Button onClick={() => navigate('/workflows', { state: { filterWorkflowId: selectedWorkflow?.workflow_id } })}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Workflows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone Report - Collapsible, default closed */}
      <Collapsible open={milestoneReportOpen} onOpenChange={setMilestoneReportOpen}>
        <Card className={cn(isBarbiePink && "card-brutalist")} data-testid="milestone-report-card">
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ChevronDown className={cn(
                    "w-5 h-5 transition-transform duration-200",
                    !milestoneReportOpen && "-rotate-90"
                  )} />
                  <AlertCircle className="w-5 h-5 text-primary" />
                  Milestone Report
                  <span className="text-sm font-normal text-muted-foreground ml-2">Next 14 Days</span>
                </CardTitle>
                <div className="flex items-center gap-3 text-sm">
                  {groupedMilestones.length > 0 && (
                    <Badge variant="secondary" className="gap-1" data-testid="milestone-workflow-count">
                      {groupedMilestones.length} workflow{groupedMilestones.length === 1 ? '' : 's'}
                    </Badge>
                  )}
                  {milestones.total_overdue > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {milestones.total_overdue} Overdue
                    </Badge>
                  )}
                  <Badge variant="outline" className="gap-1">
                    <Clock className="w-3 h-3" />
                    {milestones.total_upcoming} Upcoming
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              {milestonesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : groupedMilestones.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No overdue or upcoming milestones in the next 14 days</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {groupedMilestones.map((row, idx) => {
                    const isExpanded = !!expandedWorkflows[row.workflow_id];
                    const hasChildren = row.total_count > 1;
                    return (
                      <div
                        key={`wf-${row.workflow_id}-${idx}`}
                        className={cn(
                          "rounded-lg border transition-colors",
                          row.is_overdue ? "bg-red-50/50 border-red-200" : "bg-background",
                        )}
                        data-testid={`milestone-wf-row-${row.workflow_id}`}
                      >
                        {/* ---- Workflow headline row ---- */}
                        <div
                          className={cn(
                            "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/40",
                            isExpanded && "border-b",
                          )}
                          onClick={(e) => {
                            // Click anywhere on the row toggles expansion;
                            // a separate "Open workflow" button below routes
                            // to the workflows page.
                            if (hasChildren) {
                              e.stopPropagation();
                              toggleWorkflowExpanded(row.workflow_id);
                            } else {
                              navigate('/workflows');
                            }
                          }}
                        >
                          {/* Expand chevron */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasChildren) toggleWorkflowExpanded(row.workflow_id);
                            }}
                            className={cn(
                              "w-6 h-6 rounded flex-shrink-0 flex items-center justify-center",
                              hasChildren ? "hover:bg-muted text-muted-foreground" : "opacity-0 pointer-events-none",
                            )}
                            data-testid={`milestone-wf-toggle-${row.workflow_id}`}
                            aria-label={isExpanded ? 'Collapse milestones' : 'Expand milestones'}
                          >
                            {isExpanded
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />}
                          </button>

                          {/* Status Indicator */}
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                            row.is_overdue ? "bg-red-100" : row.days_until === 0 ? "bg-amber-100" : "bg-blue-100",
                          )}>
                            {row.is_overdue
                              ? <AlertTriangle className="w-5 h-5 text-red-600" />
                              : row.days_until === 0
                                ? <AlertCircle className="w-5 h-5 text-amber-600" />
                                : <Clock className="w-5 h-5 text-blue-600" />}
                          </div>

                          {/* Headline content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="font-medium text-foreground truncate">{row.workflow_name}</span>
                              <Badge className={cn("text-xs", getCategoryColor(row.category))}>
                                {getCategoryLabel(row.category)}
                              </Badge>
                              {row.overdue_count > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {row.overdue_count} overdue
                                </Badge>
                              )}
                              {row.upcoming_count > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {row.upcoming_count} upcoming
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Building2 className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{row.org_name}</span>
                              <span className="text-muted-foreground/50">•</span>
                              <span className="truncate">Next: {row.milestone_name}</span>
                            </div>
                          </div>

                          {/* Headline due date */}
                          <div className="text-right flex-shrink-0">
                            <p className={cn(
                              "font-medium text-sm",
                              row.is_overdue ? "text-red-600" : row.days_until === 0 ? "text-amber-600" : "text-foreground",
                            )}>
                              {getDaysLabel(row.days_until)}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatDate(row.due_date)}</p>
                          </div>
                        </div>

                        {/* ---- Expanded milestones list ---- */}
                        {isExpanded && hasChildren && (
                          <div className="divide-y divide-slate-100 bg-muted/20">
                            {row.items.map((ms, mIdx) => (
                              <div
                                key={`${ms.workflow_id}-${ms.milestone_id}-${mIdx}`}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-muted/40 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); navigate('/workflows'); }}
                              >
                                <div className={cn(
                                  "w-2 h-2 rounded-full flex-shrink-0",
                                  ms.is_overdue ? "bg-red-500" : ms.days_until === 0 ? "bg-amber-500" : "bg-blue-500",
                                )} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm truncate">{ms.milestone_name}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className={cn(
                                    "text-xs",
                                    ms.is_overdue ? "text-red-600" : ms.days_until === 0 ? "text-amber-600" : "text-muted-foreground",
                                  )}>
                                    {getDaysLabel(ms.days_until)} · {formatDate(ms.due_date)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default DashboardPage;
